import { Button, Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { ICPAnalysisMethod } from "../../types";

interface ICPInputActionsProps {
  /** The analysis method this component is associated with */
  analysisMethod: ICPAnalysisMethod;
  /** Callback to switch to analysis tab */
  onViewAnalysis: () => void;
}

/**
 * Reusable component for ICP input actions (discard changes and view analysis)
 * Handles individual input-level state management
 */
export const ICPInputActions: FC<ICPInputActionsProps> = ({
  analysisMethod,
  onViewAnalysis,
}) => {
  const icpAnalysisMethod = useWorkspaceStore(
    (state) => state.icpAnalysisMethod
  );
  const icpSelectedProducts = useWorkspaceStore(
    (state) => state.icpSelectedProducts
  );
  const icpCustomerListData = useWorkspaceStore(
    (state) => state.icpCustomerListData
  );
  const persisted = useWorkspaceStore((state) => state.persisted);
  const hasUnsavedChanges = useWorkspaceStore(
    (state) => state.hasUnsavedChanges
  );
  const discardChanges = useWorkspaceStore((state) => state.discardChanges);

  const handleDiscardChanges = () => {
    discardChanges();
  };

  // Check if current state matches persisted state for this specific analysis method
  const hasMethodSpecificChanges = useMemo(() => {
    if (!persisted?.configuration?.icp) {
      // No persisted ICP config, so any current data represents changes
      const hasCurrentMethodData =
        analysisMethod === ICPAnalysisMethod.PRODUCT_BASED
          ? icpSelectedProducts.length > 0
          : !!icpCustomerListData?.companyMatch?.lastMatched;

      // Also check if the analysis method itself has changed from default
      const methodChanged =
        icpAnalysisMethod !== ICPAnalysisMethod.PRODUCT_BASED;

      return hasCurrentMethodData || methodChanged;
    }

    const persistedIcp = persisted.configuration.icp;

    // Check if analysis method has changed
    const persistedMethod = persistedIcp.customerMatch
      ? ICPAnalysisMethod.CUSTOMER_LIST
      : ICPAnalysisMethod.PRODUCT_BASED;

    if (icpAnalysisMethod !== persistedMethod) {
      return true;
    }

    if (analysisMethod === ICPAnalysisMethod.PRODUCT_BASED) {
      const persistedProducts = persistedIcp.productIds || [];
      return (
        JSON.stringify(icpSelectedProducts.sort()) !==
        JSON.stringify(persistedProducts.sort())
      );
    } else {
      const persistedCustomerMatch = persistedIcp.customerMatch;
      const currentCustomerMatch = icpCustomerListData?.companyMatch;

      if (!persistedCustomerMatch && !currentCustomerMatch?.lastMatched) {
        return false;
      }
      if (!persistedCustomerMatch && currentCustomerMatch?.lastMatched) {
        return true;
      }
      if (persistedCustomerMatch && !currentCustomerMatch?.lastMatched) {
        return true;
      }

      return persistedCustomerMatch?.fileId !== currentCustomerMatch?.fileId;
    }
  }, [
    analysisMethod,
    icpAnalysisMethod,
    icpSelectedProducts,
    icpCustomerListData,
    persisted,
  ]);

  const isCurrentMethod = icpAnalysisMethod === analysisMethod;
  const hasData =
    analysisMethod === ICPAnalysisMethod.PRODUCT_BASED
      ? icpSelectedProducts.length > 0
      : icpCustomerListData?.companyMatch?.lastMatched;

  const isViewAnalysisDisabled = !isCurrentMethod || !hasData;
  const isDiscardDisabled =
    !isCurrentMethod || !hasUnsavedChanges || !hasMethodSpecificChanges;

  return (
    <Stack
      direction="row"
      spacing={2}
      padding={2}
      paddingLeft={0}
      paddingTop={analysisMethod === ICPAnalysisMethod.PRODUCT_BASED ? 5 : 3}
    >
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleDiscardChanges}
        disabled={isDiscardDisabled}
      >
        DISCARD CHANGES
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={onViewAnalysis}
        disabled={isViewAnalysisDisabled}
      >
        VIEW ANALYSIS
      </Button>
    </Stack>
  );
};
